import { Component, ElementRef, Inject, NgZone, ViewChild } from '@angular/core';
import { IModalsService, IMODALS_SERVICE } from '@platform500services/p500-ui-kit';
import { DestroySubscribers } from '../../decorators/destroy-subscribers.decorator';
import { IUnsecuredService, IUnsecuredStatus } from '../../services/unsecured/unsecured-service.interface';
import { UserActivityService } from '../../services/user-activity/user-activity.service';
import { filter, finalize, map, switchMap, takeUntil, takeWhile, tap } from 'rxjs/operators';
import { Subject, Subscription, timer } from 'rxjs';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'gtd-unsecured-bar',
  templateUrl: './unsecured-bar.component.html',
  providers: [DatePipe],
})
@DestroySubscribers()
export class UnsecuredBarComponent {
  @ViewChild('timer', { static: false }) timeElement: ElementRef;

  public isLoading: boolean;
  public unsecuredStatus: IUnsecuredStatus;
  public timeLeft: number | null = null;

  private stopTimer$ = new Subject<void>();
  private timer$ = this.ngZone.runOutsideAngular(() =>
    timer(0, 1000).pipe(
      map((tick) => {
        this.timeLeft = 5000 - tick * 1000;
        this.timeElement.nativeElement.innerText = this.datePipe.transform(this.timeLeft, 'mm:ss');
        return this.timeLeft;
      }),
      takeWhile((value) => value > 0),
      takeUntil(this.stopTimer$),
      finalize(() => {
        this.timeElement.nativeElement.innerText = '';
      })
    )
  );
  private subscribers: Record<string, Subscription> = {};

  constructor(
    @Inject('IUnsecuredService') public unsecuredService: IUnsecuredService,
    @Inject(IMODALS_SERVICE) private modalsService: IModalsService,
    private userActivityService: UserActivityService,
    private datePipe: DatePipe,
    private ngZone: NgZone
  ) {
    this.init();
  }

  init(): void {
    this.isLoading = true;
    this.subscribers.status = this.unsecuredService
      .getUnsecuredStatus()
      .pipe(
        tap((status) => {
          this.unsecuredStatus = status;
          if (status.isUnsecured && status.unsecuredBefore) {
            this.isLoading = false;
          }
        }),
        filter((status) => status.isUnsecured && !!status.unsecuredBefore),
        switchMap(() => {
          return this.userActivityService.userActivityEnded$();
        })
      )
      .subscribe(() => {
        this.leaveMode();
      });
    this.subscribers.timer = this.userActivityService
      .userActivitySoonEnded$()
      .pipe(
        filter(() => this.unsecuredStatus.isUnsecured && !!this.unsecuredStatus.unsecuredBefore),
        switchMap(() => {
          return this.timer$;
        })
      )
      .subscribe();
    this.subscribers.userActivity = this.userActivityService.userActivity$().subscribe(() => {
      this.stopTimer$.next();
    });
  }

  leaveMode(): void {
    this.isLoading = true;
    this.unsecuredService.leave().subscribe((response) => {
      if (response.data && response.data.user) {
        this.unsecuredService.setUnsecuredStatus(false);
        this.modalsService.closeAll();
      }
    });
  }
}
